import { Injectable } from '@angular/core';
import { BehaviorSubject, map } from 'rxjs';
import {
    FieldDefinitionOption,
    FormDefinition,
    SummaryLevel,
} from '@wdx/shared/utils';
import { FormSummaryService } from '../form-summary/form-summary.service';
import * as R from 'ramda';
import { WdxAuditValue } from '@wdx/shared/components/wdx-audit';

export type SummaryInfoCardValue = {
    title: string;
    subTitle: string;
    icon: string;
    colour: string;
};

export type SummaryInfoCard = Omit<FieldDefinitionOption, 'value'> & {
    name?: string;
    value: string;
    iconBackground?: boolean;
    iconColour?: string;
    bgColour?: string;
    textColour?: string;
};

@Injectable()
export class FormSummaryLayoutService {
    summaryInfoCards$ = new BehaviorSubject<SummaryInfoCard[]>([]);
    infoCards$ = new BehaviorSubject<SummaryInfoCard[]>([]);

    constructor(private formSummaryService: FormSummaryService) {}

    /**
     * Returns summary info cards from a given form definition schema and data
     */
    getSummaryInfoCards(definition: FormDefinition, formData: any): void {
        // This is to reset to observable data
        this.summaryInfoCards$.next([]);
        this.infoCards$.next([]);

        const infoCards = definition.schema
            ?.filter((item) => item.summaryLevel === SummaryLevel.InfoCard)
            .map((summaryField) => {
                const summaryLayoutItem =
                    definition.layout?.sectionLayoutDefinitions
                        ?.find((section) =>
                            section.elementLayoutDefinitions?.find(
                                (element) => element.name === summaryField.name,
                            ),
                        )
                        ?.elementLayoutDefinitions?.find(
                            (element) => element.name === summaryField.name,
                        );
                const value = formData[summaryField.name as string];

                if (value && Object.hasOwn(value, 'title')) {
                    const textColour = value.colour
                        ? ['success', 'warning', 'danger'].includes(
                              value.colour,
                          )
                            ? 'text-success-contrast'
                            : ''
                        : '';
                    const bgColour = value.colour ? `bg-${value.colour}` : '';
                    return {
                        label: value.subTitle ?? summaryLayoutItem?.label,
                        value: value.title,
                        icon: value.icon ?? summaryField?.summaryIcon,
                        name: summaryField?.name as string,
                        bgColour,
                        iconBackground: textColour === '',
                        iconColour: textColour,
                        textColour,
                        summaryField,
                    };
                }

                return {
                    label: summaryLayoutItem?.label,
                    value,
                    icon: summaryField?.summaryIcon,
                    summaryField,
                    name: summaryField?.name as string,
                };
            })
            .filter((infoCard) => !R.isNil(infoCard.value));

        if (infoCards?.length) {
            this.infoCards$.next(infoCards);
            infoCards.forEach((infoCard) => {
                this.formSummaryService
                    .getStringValue(
                        formData[infoCard.summaryField.name as string],
                        infoCard.summaryField,
                        null as any,
                        null,
                        definition?.schema,
                    )
                    .pipe(
                        map((getStringValue) => {
                            this.summaryInfoCards$.next([
                                ...this.summaryInfoCards$.value,
                                {
                                    label: infoCard?.label,
                                    value: getStringValue as string,
                                    icon: infoCard.summaryField?.summaryIcon,
                                    name: infoCard?.name as string,
                                    bgColour: infoCard?.bgColour ?? '',
                                    iconBackground:
                                        infoCard?.iconBackground ?? false,
                                    iconColour: infoCard?.iconColour ?? '',
                                    textColour: infoCard?.textColour ?? '',
                                },
                            ]);
                        }),
                    )
                    .subscribe();
            });
        }
    }
}
